import { createFileRoute, Link } from '@tanstack/react-router'
import { capitalize } from 'lodash-es'
import type { ReactNode } from 'react'

import type { DayOfWeekType, Promotion, TimeOfDay } from '@/api/admin-promotions-types'
import { useDeletePromotion } from '@/api/api-mutations'
import {
	preloadGetPromotion,
	useGetCustomer,
	useGetCustomerGroup,
	useGetDashboardFeatures,
	useGetPromotion
} from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { toast } from '@/components/base/toast'
import { Tooltip } from '@/components/base/tooltip'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Trash } from '@/icons/trash'
import { fromPrecision } from '@/utils/currency'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

import { ApplicationTargetsTable, toGroupTargetRows, toUuidTargetRows } from './-application-targets-table'
import { DAY_ORDER, getPromotionScheduleStatus, storedPercentageToPercentPoints } from './-promotion-utils'

export const Route = createFileRoute('/_authenticated/_general/promotions/$promotionUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<PromotionDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { promotionUuid } }) => {
		await preloadGetPromotion(context, { path: { promotionUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Promotions.Detail.Title()
	}
})

const formatTime = (time: TimeOfDay) => {
	return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`
}

function PromotionDetail() {
	const { L } = useI18n()
	const { promotionUuid } = Route.useParams()
	const navigate = Route.useNavigate()

	const promotion = useGetPromotion({ promotionUuid })
	const deletePromotion = useDeletePromotion()
	const prompt = usePrompt()

	const handleDelete = async () => {
		const res = await prompt({
			title: L.Promotions.Delete.Title(),
			description: L.Promotions.Delete.Description({ name: promotion.data?.name ?? '' }),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!res) {
			return
		}
		const result = await deletePromotion({ path: { promotionUuid } })
		if (result.isSuccess) {
			toast.success(L.Promotions.Delete.SuccessToast({ name: promotion.data?.name ?? '' }))
			navigate({ to: '/promotions', replace: true })
		} else {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
		}
	}

	const dayOfWeekLabel = (day: DayOfWeekType): string => {
		switch (day) {
			case 'MONDAY':
				return L.DaysOfWeek.Monday()
			case 'TUESDAY':
				return L.DaysOfWeek.Tuesday()
			case 'WEDNESDAY':
				return L.DaysOfWeek.Wednesday()
			case 'THURSDAY':
				return L.DaysOfWeek.Thursday()
			case 'FRIDAY':
				return L.DaysOfWeek.Friday()
			case 'SATURDAY':
				return L.DaysOfWeek.Saturday()
			case 'SUNDAY':
				return L.DaysOfWeek.Sunday()
			default:
				return capitalize(day)
		}
	}

	const status = promotion.data
		? getPromotionScheduleStatus(promotion.data, L.General)
		: { text: L.General.Unknown(), color: 'grey' as StatusColor }

	const bundleRule =
		promotion.data?.applicationRule?.type === 'X_ITEMS_FROM_Y_PRODUCT_GROUPS'
			? promotion.data.applicationRule
			: undefined
	const isBundle = !!bundleRule
	const buyXGetYRule =
		promotion.data?.applicationRule?.type === 'BUY_X_GET_Y' ? promotion.data.applicationRule : undefined
	const isBuyXGetY = !!buyXGetYRule
	// Buy-X-get-Y carries the discount on each benefit entry rather than on the promotion.
	const benefitDef = buyXGetYRule?.benefit[0]?.discount

	// Targets are additive, so list every kind the promotion actually carries rather than picking one.
	const appliesToSummary = () => {
		if (promotion.data?.applyToAllProducts) {
			return L.Promotions.Create.ApplyToAll()
		}
		const kinds: string[] = []
		if ((promotion.data?.productGroupUuids?.length ?? 0) > 0) {
			kinds.push(L.Promotions.Create.ApplyToGroups())
		}
		if ((promotion.data?.productCategoryUuids?.length ?? 0) > 0) {
			kinds.push(L.Promotions.Create.ApplyToCategories())
		}
		if ((promotion.data?.productUuids?.length ?? 0) > 0) {
			kinds.push(L.Promotions.Create.ApplyToProducts())
		}
		return kinds.join(', ')
	}

	const formatDefinitionValue = (def?: typeof benefitDef) => {
		if (!def) return L.General.NotSet()
		if (def.type === 'PERCENTAGE') {
			return `${storedPercentageToPercentPoints(def.percentage)}%`
		}
		if (def.type === 'FIXED_AMOUNT') {
			return `${fromPrecision(def.amount)} ${def.currencyCode}`
		}
		return L.General.NotSet()
	}
	const formatDiscountValue = () =>
		formatDefinitionValue(buyXGetYRule ? benefitDef : promotion.data?.discountDefinition)

	const unitsEnabled = useGetDashboardFeatures().data?.unitsOfMeasureEnabled === true
	const locked = promotion.data?.thirdPartySource
		? L.Promotions.ManagedExternally({ source: L.Enum.ThirdPartySource(promotion.data.thirdPartySource) })
		: undefined

	return (
		<SingleColumnPage hasOutlet>
			{/* General */}
			<Container className='divide-y'>
				<ContainerHeader
					heading={promotion.data?.name || L.Promotions.Detail.Title()}
					rightSide={
						locked ? (
							<LockedIconButton label={locked}>
								<PencilSquare />
							</LockedIconButton>
						) : (
							<Link from={Route.fullPath} to='edit'>
								<IconButton tooltipText={L.General.Edit()}>
									<PencilSquare />
								</IconButton>
							</Link>
						)
					}
					actions={[
						{
							actions: [
								{
									label: L.General.Delete(),
									icon: <Trash />,
									onClick: handleDelete
								}
							]
						}
					]}
				/>
				<SectionRow
					title={L.Fields.Status()}
					value={<StatusCell color={status.color}>{status.text}</StatusCell>}
				/>
				<SectionRow
					title={L.Promotions.Create.Rank()}
					value={promotion.data != null ? String(promotion.data.rank) : L.General.NotSet()}
				/>
				<SectionRow title={L.Fields.ExternalId()} value={promotion.data?.externalId || L.General.NotSet()} />
				{promotion.data?.promotionGroupName ? (
					<SectionRow title={L.Promotions.Group()} value={promotion.data.promotionGroupName} />
				) : null}
				{promotion.data?.displayTitle ? (
					<SectionRow title={L.Promotions.Detail.DisplayTitle()} value={promotion.data.displayTitle} />
				) : null}
				<SectionRow
					title={L.Promotions.Create.SalesChannelGroups()}
					value={
						promotion.data?.salesChannelGroups && promotion.data.salesChannelGroups.length > 0
							? promotion.data.salesChannelGroups.map(group => (
									<Badge key={group.uuid}>{group.name}</Badge>
								))
							: null
					}
				/>
				<SectionRow
					title={L.Promotions.Detail.SalesChannels()}
					value={
						promotion.data?.salesChannels && promotion.data.salesChannels.length > 0
							? promotion.data.salesChannels.map(channel => (
									<Badge key={channel.uuid}>{channel.name}</Badge>
								))
							: null
					}
				/>
			</Container>

			{/* Schedule */}
			<Container className='divide-y'>
				<ContainerHeader heading={L.Promotions.Detail.Schedule()} />
				<SectionRow
					title={L.Fields.StartDate()}
					value={
						promotion.data?.startDate
							? new Date(promotion.data.startDate).toLocaleDateString()
							: L.General.NotSet()
					}
				/>
				<SectionRow
					title={L.Fields.EndDate()}
					value={
						promotion.data?.endDate
							? new Date(promotion.data.endDate).toLocaleDateString()
							: L.General.NotSet()
					}
				/>
				{promotion.data?.daysOfWeek && promotion.data.daysOfWeek.length > 0 && (
					<SectionRow
						title={L.Promotions.Detail.DaysOfWeek()}
						value={(() => {
							// Group days that share the same time window into one badge, e.g.
							// "Mon, Tue, Wed (09:00 - 12:00)" instead of one badge per day.
							const windows: { time: string; days: DayOfWeekType[] }[] = []
							for (const dayObj of promotion.data.daysOfWeek) {
								const time =
									dayObj.activeFromTime || dayObj.activeToTime
										? `${dayObj.activeFromTime ? formatTime(dayObj.activeFromTime) : '00:00'} - ${dayObj.activeToTime ? formatTime(dayObj.activeToTime) : '23:59'}`
										: ''
								const existing = windows.find(w => w.time === time)
								if (existing) {
									existing.days.push(dayObj.dayOfWeek)
								} else {
									windows.push({ time, days: [dayObj.dayOfWeek] })
								}
							}
							return windows.map(w => {
								const days = [...w.days]
									.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
									.map(dayOfWeekLabel)
									.join(', ')
								return (
									<Badge
										key={w.time || 'all-day'}
										className='h-auto max-w-full items-start whitespace-normal py-1 text-left'
									>
										{w.time ? `${days} (${w.time})` : days}
									</Badge>
								)
							})
						})()}
					/>
				)}
			</Container>

			{/* Effect */}
			<Container className='divide-y'>
				<ContainerHeader heading={L.Promotions.Detail.Discount()} />
				<SectionRow
					title={L.Promotions.Create.PromotionType()}
					value={
						isBuyXGetY
							? L.Promotions.Create.TypeBuyXGetY()
							: isBundle
								? L.Promotions.Create.TypeBundle()
								: L.Promotions.Create.TypeDiscount()
					}
				/>
				{promotion.data?.shouldOverridePrice ? (
					<SectionRow
						title={L.Promotions.Detail.DiscountType()}
						value={L.Promotions.Create.OverridePrice()}
					/>
				) : (
					<>
						<SectionRow
							title={L.Promotions.Detail.DiscountType()}
							value={
								(isBuyXGetY ? benefitDef?.type : promotion.data?.discountDefinition?.type) ===
								'PERCENTAGE'
									? L.Promotions.Create.Percentage()
									: L.Promotions.Create.FixedAmount()
							}
						/>
						<SectionRow title={L.Promotions.Detail.DiscountValue()} value={formatDiscountValue()} />
					</>
				)}
				{promotion.data?.shouldOverridePrice && (
					<SectionRow
						title={L.Promotions.Detail.IncludeFees()}
						value={promotion.data.includeFees ? L.General.True() : L.General.False()}
					/>
				)}
			</Container>

			{/* Override prices */}
			{promotion.data?.shouldOverridePrice && promotion.data.prices && promotion.data.prices.length > 0 && (
				<Container className='divide-y'>
					<ContainerHeader heading={L.Promotions.Detail.Prices()} />
					{[...promotion.data.prices]
						.sort((a, b) => {
							const aIsGroup = a.salesChannelGroup != null
							const bIsGroup = b.salesChannelGroup != null
							if (aIsGroup === bIsGroup) {
								return 0
							}
							return aIsGroup ? -1 : 1
						})
						.map(price => (
							<SectionRow
								key={price.salesChannel?.uuid ?? price.salesChannelGroup?.uuid}
								title={price.salesChannelGroup?.name ?? price.salesChannel?.name ?? ''}
								value={
									price.priceInfo.variablePrice
										? L.General.NotSet()
										: `${fromPrecision(price.priceInfo.amount)} ${price.currencyCode}`
								}
							/>
						))}
				</Container>
			)}

			{/* Application */}
			<Container className='overflow-hidden'>
				<ContainerHeader heading={L.Promotions.Detail.ApplicationRule()} />
				{isBundle && bundleRule ? (
					<ApplicationTargetsTable
						heading={L.Promotions.Create.BundleGroups()}
						kind='group'
						showQuantity
						rows={toGroupTargetRows(bundleRule.products)}
					/>
				) : isBuyXGetY && buyXGetYRule ? (
					<>
						<ApplicationTargetsTable
							heading={L.Promotions.Create.QualifyingGroups()}
							kind='group'
							showQuantity
							rows={toGroupTargetRows(buyXGetYRule.qualifying)}
						/>
						<ApplicationTargetsTable
							heading={L.Promotions.Create.BenefitGroups()}
							kind='group'
							showQuantity
							rows={toGroupTargetRows(buyXGetYRule.benefit)}
						/>
					</>
				) : (
					<>
						<SectionRow title={L.Promotions.Detail.AppliesTo()} value={appliesToSummary()} />
						{promotion.data?.productGroupUuids && promotion.data.productGroupUuids.length > 0 && (
							<ApplicationTargetsTable
								heading={L.Promotions.Detail.ProductGroups()}
								kind='group'
								rows={toUuidTargetRows(promotion.data.productGroupUuids)}
							/>
						)}
						{promotion.data?.productCategoryUuids && promotion.data.productCategoryUuids.length > 0 && (
							<ApplicationTargetsTable
								heading={L.Promotions.Create.Categories()}
								kind='category'
								rows={toUuidTargetRows(promotion.data.productCategoryUuids)}
							/>
						)}
						{promotion.data?.productUuids && promotion.data.productUuids.length > 0 && (
							<ApplicationTargetsTable
								heading={L.Promotions.Create.Products()}
								kind='product'
								rows={toUuidTargetRows(promotion.data.productUuids)}
							/>
						)}
					</>
				)}
				{unitsEnabled ? (
					<SectionRow title={L.Promotions.Detail.SellingUnit()} value={sellingUnitLabel(promotion.data, L)} />
				) : null}
			</Container>

			{/* Customer eligibility */}
			<Container className='divide-y'>
				<ContainerHeader heading={L.Promotions.Detail.CustomerEligibility()} />
				{(promotion.data?.customerGroupUuids?.length ?? 0) === 0 &&
				(promotion.data?.customerUuids?.length ?? 0) === 0 ? (
					<SectionRow
						title={L.Promotions.Detail.AppliesTo()}
						value={L.Promotions.Detail.AppliesToEveryone()}
					/>
				) : (
					<>
						{promotion.data?.customerGroupUuids && promotion.data.customerGroupUuids.length > 0 && (
							<SectionRow
								title={L.Promotions.Create.CustomerGroups()}
								value={
									<div className='flex flex-wrap gap-1'>
										{promotion.data.customerGroupUuids.map(uuid => (
											<CustomerGroupBadge key={uuid} uuid={uuid} />
										))}
									</div>
								}
							/>
						)}
						{promotion.data?.customerUuids && promotion.data.customerUuids.length > 0 && (
							<SectionRow
								title={L.Promotions.Create.Customers()}
								value={
									<div className='flex flex-wrap gap-1'>
										{promotion.data.customerUuids.map(uuid => (
											<CustomerBadge key={uuid} uuid={uuid} />
										))}
									</div>
								}
							/>
						)}
					</>
				)}
			</Container>
		</SingleColumnPage>
	)
}

function LockedIconButton({ label, children }: { label: string; children: ReactNode }) {
	return (
		<Tooltip content={label}>
			<span className='inline-flex'>
				<IconButton disabled className='pointer-events-none shrink-0'>
					{children}
				</IconButton>
			</span>
		</Tooltip>
	)
}

function sellingUnitLabel(
	promotion: Promotion | undefined,
	L: { Promotions: { Create: { AllUnits: () => string; BaseUnit: () => string } } }
) {
	if (!promotion || promotion.appliesToAllUnits) {
		return L.Promotions.Create.AllUnits()
	}
	if (!promotion.unitOfMeasure) {
		return L.Promotions.Create.BaseUnit()
	}
	return promotion.unitOfMeasure.name
		? `${promotion.unitOfMeasure.code} (${promotion.unitOfMeasure.name})`
		: promotion.unitOfMeasure.code
}

function CustomerGroupBadge({ uuid }: { uuid: string }) {
	const group = useGetCustomerGroup({ customerGroupUuid: uuid })
	return <Badge>{group.data?.name ?? uuid}</Badge>
}

function CustomerBadge({ uuid }: { uuid: string }) {
	const customer = useGetCustomer({ customerUuid: uuid })
	const c = customer.data
	const label = c ? [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || c.email || c.phone || uuid : uuid
	return <Badge>{label}</Badge>
}
